/**
 * FaelligText — ported from v0 components/brokly/faellig-text.tsx.
 * Maps restTage → countdown text with semantic colour tone.
 * Token mapping: v0 text-{tone} → our text-sem-{tone}.
 */
import { CalendarClock, AlertTriangle } from 'lucide-react';
import { cn } from '../../../lib/cn';
import type { StatusTone } from '../../../components/ui/StatusDot';

export interface FaelligInfoResult {
  label: string;
  tone: StatusTone;
  overdue: boolean;
}

export function faelligInfo(restTage: number): FaelligInfoResult {
  if (restTage < 0) {
    const n = Math.abs(restTage);
    return { label: `überfällig seit ${n} ${n === 1 ? 'Tag' : 'Tagen'}`, tone: 'alert', overdue: true };
  }
  if (restTage === 0) return { label: 'heute fällig', tone: 'decide', overdue: false };
  if (restTage === 1) return { label: 'in 1 Tag', tone: 'decide', overdue: false };
  return { label: `in ${restTage} Tagen`, tone: restTage <= 2 ? 'decide' : 'neutral', overdue: false };
}

const TONE_TEXT: Record<StatusTone, string> = {
  alert: 'text-sem-alert',
  decide: 'text-sem-decide',
  info: 'text-sem-info',
  ok: 'text-sem-ok',
  neutral: 'text-muted-foreground',
  // U1: StatusTone gained the contract names (danger/warning/success) — the
  // sem-* aliases above resolve to the same --status-* tokens since U1.
  danger: 'text-status-danger',
  warning: 'text-status-warning',
  success: 'text-status-success',
};

export function FaelligText({
  restTage,
  className,
  showIcon = true,
}: {
  restTage: number;
  className?: string;
  showIcon?: boolean;
}) {
  const info = faelligInfo(restTage);
  const Icon = info.overdue ? AlertTriangle : CalendarClock;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-sm tabular-nums',
        TONE_TEXT[info.tone],
        info.overdue && 'font-medium',
        className,
      )}
    >
      {showIcon ? <Icon className="size-3.5 shrink-0" /> : null}
      {info.label}
    </span>
  );
}
